<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import CloseCircleMemberCard from './CloseCircleMemberCard.vue';
import CloseCircleMemberDetail from './CloseCircleMemberDetail.vue';
import CloseCircleMemberFormSheet from './CloseCircleMemberFormSheet.vue';
import { useCloseCircleState } from './useCloseCircleState.js';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  contactName: {
    type: String,
    default: '',
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
  projects: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'closeSheet',
  'createProjectForMember',
  'openProject',
]);

const { t } = useI18n();
const closeCircle = useCloseCircleState();
const formSheetRef = ref(null);
const selectedMemberId = ref(null);
const openedFrom = ref('list');
const formOpenedFrom = ref('list');

const contact = computed(() => ({
  id: props.contactId,
  name: props.contactName,
  phone: props.contactPhone,
  email: props.contactEmail,
}));

const members = computed(() => closeCircle.membersForContact(props.contactId));
const isLoading = computed(() => closeCircle.isLoading(props.contactId));
const error = computed(() => closeCircle.errorForContact(props.contactId));

const selectedMember = computed(() =>
  selectedMemberId.value ? closeCircle.findMember(selectedMemberId.value) : null
);

const loadMembers = () => closeCircle.loadMembers(props.contactId);

onMounted(loadMembers);
watch(() => props.contactId, loadMembers);

const openMember = (member, source = 'direct') => {
  openedFrom.value = source;
  selectedMemberId.value = member.id;
};

const showList = () => {
  selectedMemberId.value = null;
};

const closeDetail = () => {
  selectedMemberId.value = null;
  if (openedFrom.value === 'direct') {
    emit('closeSheet');
  }
};

const openNewMember = (initial = {}, source = 'direct') => {
  formOpenedFrom.value = source;
  formSheetRef.value?.open(initial);
};

const openEditMember = member => {
  formOpenedFrom.value = 'detail';
  formSheetRef.value?.open({}, member);
};

const openAddImportantDate = member => {
  formOpenedFrom.value = 'detail';
  formSheetRef.value?.open({}, member, { addImportantDate: true });
};

const saveMember = async ({ memberId, values, onSuccess, onError }) => {
  try {
    const saved = memberId
      ? await closeCircle.updateMember(memberId, values)
      : await closeCircle.createMember(props.contactId, values);
    if (saved) selectedMemberId.value = saved.id;
    onSuccess?.();
  } catch (saveError) {
    onError?.(saveError);
  }
};

const onFormClosed = ({ reason }) => {
  if (formOpenedFrom.value === 'direct' && reason !== 'saved') {
    emit('closeSheet');
  }
};

const detachMember = async member => {
  await closeCircle.detachMember(member.id);
  selectedMemberId.value = null;
};

const createProjectForMember = member => {
  emit('createProjectForMember', member);
  emit('closeSheet');
};

defineExpose({
  openMember,
  openNewMember,
  showList,
});
</script>

<template>
  <div class="flex min-h-0 flex-col">
    <CloseCircleMemberDetail
      v-if="selectedMember"
      :member="selectedMember"
      :contact="contact"
      :projects="projects"
      @back="closeDetail"
      @edit="openEditMember"
      @add-important-date="openAddImportantDate"
      @detach="detachMember"
      @create-project="createProjectForMember"
      @open-project="project => emit('openProject', project)"
    />

    <template v-else>
      <div class="flex items-center justify-between px-4 pb-3 pt-4">
        <div class="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            @click="emit('closeSheet')"
          />
          <div class="min-w-0">
            <h2 class="truncate text-base font-semibold text-n-slate-12">
              {{ t('CLOSE_CIRCLE.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ t('CLOSE_CIRCLE.MEMBER_COUNT', { count: members.length }) }}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          class="!rounded-full bg-n-alpha-black2 shrink-0"
          @click="openNewMember({}, 'list')"
        />
      </div>

      <div v-if="isLoading && !members.length" class="px-4 py-6">
        <p class="text-sm text-n-slate-11">
          {{ t('CLOSE_CIRCLE.LOADING') }}
        </p>
      </div>

      <div
        v-else-if="error && !members.length"
        class="flex flex-col gap-3 px-4 py-6"
      >
        <p class="text-sm text-n-ruby-11">
          {{ t('CLOSE_CIRCLE.LOAD_ERROR') }}
        </p>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          :label="t('CLOSE_CIRCLE.RETRY')"
          @click="loadMembers"
        />
      </div>

      <div v-else-if="members.length" class="flex flex-col">
        <template v-for="(member, index) in members" :key="member.id">
          <div v-if="index > 0" class="mx-4 h-px bg-n-weak" />
          <CloseCircleMemberCard
            :member="member"
            @open="openMember(member, 'list')"
          />
        </template>
      </div>

      <div
        v-else
        class="flex flex-col items-center gap-3 px-6 py-10 text-center"
      >
        <span
          class="i-lucide-users-round flex size-10 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-10"
        />
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('CLOSE_CIRCLE.EMPTY_TITLE') }}
          </h3>
          <p class="text-sm leading-relaxed text-n-slate-11">
            {{ t('CLOSE_CIRCLE.EMPTY_DESCRIPTION') }}
          </p>
        </div>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          :label="t('CLOSE_CIRCLE.ADD')"
          @click="openNewMember({}, 'list')"
        />
      </div>
    </template>

    <CloseCircleMemberFormSheet
      ref="formSheetRef"
      @save="saveMember"
      @closed="onFormClosed"
    />
  </div>
</template>
